import { BrandingConfig } from '@photobooth/types';

/**
 * Apply branding configuration to the DOM
 * Sets CSS variables that control the app's appearance
 */
export function applyBrandingToDOM(branding: BrandingConfig): void {
  const root = document.documentElement;

  // Apply colors
  root.style.setProperty('--color-primary', branding.colors.primary);
  root.style.setProperty('--color-secondary', branding.colors.secondary);
  root.style.setProperty('--color-accent', branding.colors.accent);
  root.style.setProperty('--color-background', branding.colors.background);
  root.style.setProperty('--color-surface', branding.colors.surface);
  root.style.setProperty('--color-text', branding.colors.text);
  root.style.setProperty('--color-text-muted', branding.colors.textMuted);

  // Apply typography
  root.style.setProperty('--font-heading', branding.fonts.heading);
  root.style.setProperty('--font-body', branding.fonts.body);
  root.style.setProperty('--font-display', branding.fonts.display);

  // Apply border radius
  const radiusMap = {
    none: '0',
    small: '0.375rem',
    medium: '0.75rem',
    large: '1.5rem',
    full: '9999px',
  };
  root.style.setProperty('--radius-default', radiusMap[branding.ui.borderRadius]);

  // Apply background image if provided
  if (branding.assets.backgroundImage) {
    root.style.setProperty('--background-image', `url(${branding.assets.backgroundImage})`);
  } else {
    root.style.removeProperty('--background-image');
  }

  // Update document title
  document.title = branding.businessName || 'Photo Booth Terminal';

  // Update theme color meta tag
  const themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (themeColorMeta) {
    themeColorMeta.setAttribute('content', branding.colors.primary);
  }
}

/**
 * Get computed branding values from CSS variables
 * Useful for canvas operations that need color values
 */
export function getComputedBranding(): {
  colors: Record<string, string>;
  fonts: Record<string, string>;
  radius: string;
} {
  const root = document.documentElement;
  const computedStyle = getComputedStyle(root);

  return {
    colors: {
      primary: computedStyle.getPropertyValue('--color-primary').trim(),
      secondary: computedStyle.getPropertyValue('--color-secondary').trim(),
      accent: computedStyle.getPropertyValue('--color-accent').trim(),
      background: computedStyle.getPropertyValue('--color-background').trim(),
      surface: computedStyle.getPropertyValue('--color-surface').trim(),
      text: computedStyle.getPropertyValue('--color-text').trim(),
      textMuted: computedStyle.getPropertyValue('--color-text-muted').trim(),
    },
    fonts: {
      heading: computedStyle.getPropertyValue('--font-heading').trim(),
      body: computedStyle.getPropertyValue('--font-body').trim(),
      display: computedStyle.getPropertyValue('--font-display').trim(),
    },
    radius: computedStyle.getPropertyValue('--radius-default').trim(),
  };
}

/**
 * Generate gradient CSS from button style
 */
export function getButtonGradient(style: BrandingConfig['ui']['buttonStyle'], colors: BrandingConfig['colors']): string {
  switch (style) {
    case 'gradient':
      return `linear-gradient(to right, ${colors.primary}, ${colors.secondary})`;
    case 'flat':
      return colors.primary;
    case 'outlined':
      return 'transparent';
    case 'soft':
      return `${colors.primary}20`; // 20% opacity
    default:
      return colors.primary;
  }
}

/**
 * Get card shadow based on card style
 */
export function getCardShadow(style: BrandingConfig['ui']['cardStyle']): string {
  switch (style) {
    case 'elevated':
      return '0 4px 16px rgba(0, 0, 0, 0.1)';
    case 'bordered':
      return 'none';
    case 'flat':
      return 'none';
    default:
      return '0 4px 16px rgba(0, 0, 0, 0.1)';
  }
}

/**
 * Get animation duration based on animation level
 */
export function getAnimationDuration(level: BrandingConfig['ui']['animationLevel']): number {
  switch (level) {
    case 'none':
      return 0;
    case 'subtle':
      return 150;
    case 'playful':
      return 300;
    default:
      return 200;
  }
}
